import { ElementRef, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SwalService, UserProfileService } from '__services';
import { isEmpty } from 'lodash';
import * as moment from 'moment';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class HelperService {

  hasChangeProfile = new Subject();
  constructor(
    private router: Router,
    private userProfileService: UserProfileService
  ) { }

  static userLang() {
    const lang = localStorage.getItem('userLang') || 'en';
    return lang
  }

  static getToday(): string {
    const date = new Date();
    const weekday = new Intl.DateTimeFormat(this.userLang(), { weekday: 'long' }).format(date);
    const day = +new Intl.DateTimeFormat(this.userLang(), { day: '2-digit' }).format(date);
    const month = new Intl.DateTimeFormat(this.userLang(), { month: 'long' }).format(date);
    const year = new Intl.DateTimeFormat(this.userLang(), { year: 'numeric' }).format(date);

    const suffix = this.getDateSuffix(day);

    return `${weekday} ${day}${suffix} ${month} ${year}`;
  }

  private static getDateSuffix(day: number): string {
    if (day >= 11 && day <= 13) {
      return 'th';
    }

    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }


  static getToday_dashboard(): string {
    const date = new Date();
    const weekday = new Intl.DateTimeFormat(this.userLang(), { weekday: 'long' }).format(date);
    const day = +new Intl.DateTimeFormat(this.userLang(), { day: '2-digit' }).format(date);
    const month = new Intl.DateTimeFormat(this.userLang(), { month: 'long' }).format(date);
    const year = new Intl.DateTimeFormat(this.userLang(), { year: 'numeric' }).format(date);
    const daySuffix = this.getDateSuffix(day);
    return `${weekday}, ${month} ${day}${daySuffix}, ${year}`;
  }

  static dateToISOString(value: any, isStartOfDay = true): string {
    const date = new Date(value);
    const time = isStartOfDay ? date.setHours(0, 0, 0, 0) : date.setHours(23, 59, 59, 999);
    return new Date(time).toISOString();
  }

  static isLogChainAdmin(url): boolean {
    return url.includes('logchain-admin');
  }

  static addDate(year: number, type: any): any {
    return moment(new Date()).add(year, type);
  }

  static subtractDate(year: number, type: any): any {
    return moment(new Date()).subtract(year, type);
  }

  static disableFormControl(formControls: any): void {
    // tslint:disable-next-line: forin
    for (const control in formControls) {
      formControls[control].disable();
    }
  }

  static uniqByKeepFirst(a: any[], key: (arg0: any) => any) {
    const seen = new Set();
    return a.filter(item => {
      const k = key(item);
      return seen.has(k) ? false : seen.add(k);
    });
  }


  static uniqByKeepLast(a: any[], key: (arg0: any) => any) {
    return [
      ...new Map(
        a.map(x => [key(x), x])
      ).values()
    ];
  }

  static groupByDate(objectArray: any): any {
    if (!objectArray?.length) {
      return;
    }

    const now = moment().startOf('days');
    const groupByDate = objectArray.reduce((acc, obj) => {
      const numOfDiff = moment(obj['createdAt']).startOf('days').diff(now, 'days');
      let label = '';
      if (numOfDiff === 0) {
        label = 'TODAY';
      } else if (numOfDiff === -1) {
        label = 'YESTERDAY';
      } else {
        label = new Date(obj['createdAt']).toLocaleDateString('en-GB');
      }
      if (!acc[label]) {
        acc[label] = [];
      }
      acc[label].push(obj);
      return acc;
    }, []);

    return Object.keys(groupByDate).map((key) => {
      return { createdDate: key.toString(), items: groupByDate[key] };
    });
  }

  static hasOwnerProperty(fieldName, obj): boolean {
    const hasOwnProperty = obj.hasOwnProperty.bind(obj);
    return hasOwnProperty(fieldName);
  }

  static downloadFile(data, fileName, mimeType): void {
      let blob;
      let url;
      blob = new Blob([data], {
        type: mimeType
      });
      url = window.URL.createObjectURL(blob);
      this.downloadURL(url, fileName);
      setTimeout(() => {
        return window.URL.revokeObjectURL(url);
      }, 500);
  }
  static downloadURL(data, fileName): void {
    const a = document.createElement('a');
    a.href = data;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  static getFileName(fileName): string {
    if (!fileName && typeof fileName !== 'string') {
      return '';
    }
    return fileName.split('/').slice(-1)[0];
  }

  static scrollIntoAnchor(anchor: string): void {
    // scrolls the element's parent container
    const parentContainer = document.querySelector(anchor);
    parentContainer.scrollIntoView(true);
  }

  static scrollToTopIntoAnchor(anchor: string, pixels = 0): void {
    const element = document.querySelector(anchor);
    element.scrollTop = pixels;
  }
  static removeEmpty(obj: any): any {
    Object.entries(obj).forEach(([key, val]) => {
      if (val && typeof val === 'object') {
        this.removeEmpty(val);
      } else if (!val && typeof val != 'boolean') {
        delete obj[key];
      }
    });
    return isEmpty(obj) ? null : obj;
  }
  /**
   *
   * @param code Container Number ex:  | follow by ISO 6346
   * @returns
   */
  static isContainer(code: string): boolean {
    // ex: valid container_no: CSQU3054383
    // REF: https://github.com/ruanmartinelli/is-container/blob/master/src/index.ts
    if (!code) return false
    if (typeof code !== 'string') return false
    // prettier-ignore
    const alphabet: { [letter: string]: number } = {
      'A': 10, 'B': 12, 'C': 13, 'D': 14, 'E': 15, 'F': 16, 'G': 17, 'H': 18, 'I': 19,
      'J': 20, 'K': 21, 'L': 23, 'M': 24, 'N': 25, 'O': 26, 'P': 27, 'Q': 28, 'R': 29,
      'S': 30, 'T': 31, 'U': 32, 'V': 34, 'W': 35, 'X': 36, 'Y': 37, 'Z': 38
    };
    code = code.toUpperCase()
    const invalidLenght = code.length !== 11
    const isISOFormat = /^[A-Z]{4}\d{7}/.test(code)
    if (invalidLenght || !isISOFormat) return false
    let sum = 0
    const checkDigit = code.substr(10)
    code
      .substr(0, 10)
      .split('')
      .map((char: string, index: number) => {
        let n = Number(char)
        if (index < 4) n = alphabet[char]
        n *= Math.pow(2, index)
        sum += n
      })
    sum %= 11
    sum %= 10
    return sum === Number(checkDigit)
  }

  /**
   *
   * @param url url to page view
   * @param opts (optional) options for url route
   * @returns
   */
    redirecTo(url: string, opts?: any) {
      this.router.navigateByUrl('/', {skipLocationChange: true}).then(()=>
      this.router.navigate([url], opts));
    }

  handleError(err: any){
    if(!err || !err.message) return;
    let err_code = err.code;
    err.message = err.message.replace("RegulatoryInvoices", "Regulatory Invoices");

    switch(err_code){
      case "SCHEMA_NOT_FOUND":
      case "ENTITY_NOT_FOUND":
        SwalService.error(err.message).then(() => {
          this.redirecTo('regulatory/list');
        });
        break;
      default:
        SwalService.error(err.message);
        break;
    }
  }

  scrollToInvalidField(el: ElementRef, elInvalid = '.ng-invalid.is-invalid') {
    setTimeout(() => {
      const invalidControl = el.nativeElement.querySelector(elInvalid);
      if (invalidControl) {
        invalidControl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 200)
  }

  handleErrorFirstLoad(err: any){
    const firstLoading = {
      status: false,
      errorMessage: err.message
    }
    if([400, 404].includes(err.status)) {
      firstLoading.status = true;
    } else {
      SwalService.warning(err.message);
    }
    return firstLoading
  }
}
